import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Tooltip, CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import dayjs from "dayjs";
import { DatePickerComponent } from "./DatePickerComponent";
import BlockIdSelect from "./BlockIdSelect";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ConfirmExclusionDialog from "./ConfirmExclusionDialog";
import { useAtom } from "jotai";
import {
	selectedClassAtom,
	unsavedChangesAtom,
	instanceRowsAtom,
	spotOrderingSelectionModelAtom,
} from "../../../../components/Atoms/atoms";
import CustomTooltip from "./CustomTooltip";
import { getSelectedIdsFromModel } from "./selectionModel";
import BulkInstructorManager from "./BulkInstructorManager";
import { courseHasNonTAInstructors, isTAOnlyBlockOption } from "./blockAssignment";
import { getMixedTARoleCourses } from "./mixedRoleSafeguards";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const EMPTY_BULK_EDIT_DRAFT = {
	surveyStart: null,
	surveyEnd: null,
	form: "",
};

const PANEL_CARD_CLASS = "rounded-xl border border-gray-400 bg-white shadow-sm";
const PANEL_MUTED_CARD_CLASS = "rounded-xl border border-gray-400 bg-gray-50 shadow-sm";
const PANEL_SECTION_LABEL_CLASS =
	"text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500";

function normalizeBulkFieldValue(value) {
	return value === null || value === undefined || value === "" ? "" : value;
}

function summarizeBulkField(values) {
	if (!values.length) {
		return {
			isMixed: false,
			isEmpty: true,
			value: "",
			uniqueCount: 0,
		};
	}

	const normalizedValues = values.map(normalizeBulkFieldValue);
	const uniqueValues = Array.from(new Set(normalizedValues));
	const value = uniqueValues.length === 1 ? uniqueValues[0] : "";

	return {
		isMixed: uniqueValues.length > 1,
		isEmpty: uniqueValues.length === 1 && value === "",
		value,
		uniqueCount: uniqueValues.length,
	};
}

function formatBulkDate(value) {
	if (!value) {
		return "not set";
	}

	return dayjs(value).format("DD/MMM/YY");
}

function getBulkSummaryMessage(summary, options) {
	const { fieldLabel, formatter = (value) => value, emptyLabel = "not set" } = options;

	if (summary.isMixed) {
		return `Selected courses: mixed ${fieldLabel}.`;
	}

	if (summary.isEmpty) {
		return `All selected courses: ${emptyLabel}.`;
	}

	return `All selected courses: ${formatter(summary.value)}.`;
}

function formatCourseIdList(courses = []) {
	return courses.map((course) => course.classid).join(", ");
}

function getBulkIssueSection(courses = [], allBlockItemsTA = {}) {
	const hasInstructorIssue = courses.some((course) => !course.roles || course.roles.length === 0);
	if (hasInstructorIssue) {
		return "instructors";
	}

	const hasFormIssue = courses.some((course) => {
		const assignedBlockId = course.form || course.blkid;
		const assignedBlock = allBlockItemsTA?.[assignedBlockId];
		return !assignedBlockId || (isTAOnlyBlockOption(assignedBlock) && courseHasNonTAInstructors(course));
	});
	if (hasFormIssue) {
		return "form";
	}

	const hasDateIssue = courses.some(
		(course) =>
			!course.survey_start ||
			!course.survey_end ||
			course.survey_start >= course.survey_end,
	);
	if (hasDateIssue) {
		return "dates";
	}

	return null;
}

function surveyEndConflictsWithExam(course, surveyEndValue) {
	if (!course?.examdate || !surveyEndValue) {
		return false;
	}

	const surveyEnd = dayjs(surveyEndValue);
	const examDate = dayjs(course.examdate);

	if (!surveyEnd.isValid() || !examDate.isValid()) {
		return false;
	}

	return !surveyEnd.isBefore(examDate, "day");
}

function CompactPanelHeader({ eyebrow, title, subtitle, badges = [], action }) {
	return (
		<div className="flex flex-col gap-3 border-b border-gray-200 pb-3 xl:flex-row xl:items-start xl:justify-between">
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<p className={PANEL_SECTION_LABEL_CLASS}>{eyebrow}</p>
					{badges.map((badge) => (
						<span
							key={badge.label}
							className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}
						>
							{badge.label}
						</span>
					))}
				</div>
				<h3 className="mt-1 text-lg font-semibold text-gray-900">{title}</h3>
				{subtitle ? <p className="mt-1 text-xs leading-5 text-gray-600">{subtitle}</p> : null}
			</div>
			{action ? <div className="shrink-0 self-start xl:self-auto">{action}</div> : null}
		</div>
	);
}

function formatCourseRoleSummary(course) {
	const roles = Array.isArray(course?.roles) ? course.roles : [];
	if (roles.length === 0) {
		return "No instructor assigned";
	}

	return roles.map((role) => `${role.name || "Unknown"} (${role.class_role || "N/A"})`).join(", ");
}

function cloneCourseRoles(roles = []) {
	return Array.isArray(roles) ? roles.map((role) => ({ ...role })) : [];
}

function getInstructorStateSnapshot(course) {
	return {
		roles: cloneCourseRoles(course?.roles),
		name: course?.name || "",
		empid: course?.empid || "",
		class_role: course?.class_role || "",
		teamsize: course?.teamsize ?? 0,
		teamName: course?.teamName || "",
	};
}

function createInstructorSnapshotMap(courses = []) {
	return courses.reduce((snapshotMap, course) => {
		snapshotMap[course.classid] = getInstructorStateSnapshot(course);
		return snapshotMap;
	}, {});
}

function instructorStateMatchesSnapshot(course, snapshot) {
	if (!snapshot) {
		return true;
	}

	return (
		JSON.stringify(Array.isArray(course?.roles) ? course.roles : []) ===
			JSON.stringify(snapshot.roles ?? []) &&
		(course?.name || "") === (snapshot.name || "") &&
		(course?.empid || "") === (snapshot.empid || "") &&
		(course?.class_role || "") === (snapshot.class_role || "") &&
		(course?.teamsize ?? 0) === (snapshot.teamsize ?? 0) &&
		(course?.teamName || "") === (snapshot.teamName || "")
	);
}

function SelectionListCard({
	courses,
	onRemove,
	isEditMode,
	allowRemoval = true,
	disableRemoval = false,
}) {
	return (
		<div className={`${PANEL_CARD_CLASS} flex shrink-0 flex-col overflow-hidden`}>
			<div
				className={`flex flex-col items-start gap-2 border-b border-gray-200 px-3 py-2.5 xl:flex-row xl:items-center xl:justify-between xl:gap-3 ${isEditMode ? "hidden" : ""}`}
			>
				<div className="min-w-0">
					<p className={PANEL_SECTION_LABEL_CLASS}>Selected Courses</p>
					<p className="mt-1 text-xs text-gray-600">
						{isEditMode
							? "The table is currently focused on these courses."
							: "Remove any course here before editing or reviewing the selection."}
					</p>
				</div>
				<span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
					{courses.length}
				</span>
			</div>
			<div className="max-h-64 overflow-y-auto px-3 py-2">
				<div className="flex flex-col gap-2">
					{courses.map((course) => {
						const roleSummary = formatCourseRoleSummary(course);

						return (
							<div
								key={course.classid}
								className="relative rounded-lg border border-gray-200 bg-white px-3 py-2"
							>
								<div className="pr-8">
									<div className="flex flex-wrap items-center gap-2">
										
										<p className="min-w-0  truncate text-sm font-semibold text-gray-900">
											{course.ctitle}
										</p><span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
											{course.classid}
										</span><span className="rounded-full bg-gray-700/10 px-2 py-0.5 text-[11px] font-semibold text-black">
											{course.form}
										</span>
										
									</div>
									<p className="mt-1 truncate text-xs text-gray-600" title={roleSummary}>
										{course.examdate && (
											<span className="rounded-full bg-secondaryPurple/10 px-2 py-0.5 text-[11px] font-semibold text-black">
												{course.examdate}
											</span>
										)}
										{course.survey_start && course.survey_end && (
											<><span>Tentative Window: </span><span className="rounded-full bg-secondaryPurple/10 px-2 py-0.5 text-[11px] font-semibold text-black">
												{course.survey_start} 
											</span> to <span className="rounded-full bg-secondaryPurple/10 px-2 py-0.5 text-[11px] font-semibold text-black">
												 {course.survey_end}
											</span></>
										)}
									</p>
									<p className="mt-1 truncate text-xs text-gray-600" title={roleSummary}>
										{roleSummary}
									</p>
								</div>
								{allowRemoval ? (
									<button
										type="button"
										disabled={disableRemoval}
										onClick={() => onRemove(course.classid)}
										className={`absolute right-2 top-2 rounded-full border p-1 transition-colors ${
											disableRemoval
												? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
												: "border-gray-200 bg-gray-50 text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
										}`}
										aria-label={`Remove ${course.classid} from selection`}
									>
										<CloseIcon sx={{ width: 16, height: 16 }} />
									</button>
								) : null}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}

function SummaryCard({ title, values, emptyLabel }) {
	return (
		<div className={`${PANEL_CARD_CLASS} p-3`}>
			<p className={PANEL_SECTION_LABEL_CLASS}>{title}</p>
			<div className="mt-2 flex max-h-40 flex-col gap-2 overflow-y-auto pr-1">
				{values.length > 0 ? (
					values.map((value) => (
						<div
							key={value}
							className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700"
						>
							{value}
						</div>
					))
				) : (
					<div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center text-xs text-gray-500">
						{emptyLabel}
					</div>
				)}
			</div>
		</div>
	);
}

function ValidationIssuesAlert({ validation, mixedRoleCourses = [] }) {
	const validationIssues = validation?.issues ?? [];
	const hasMixedRoleCourses = mixedRoleCourses.length > 0;

	if (!validationIssues.length && !hasMixedRoleCourses) {
		return null;
	}

	return (
		<div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
			<div className="flex items-start gap-2">
				<WarningIcon fontSize="small" className="mt-0.5 shrink-0" />
				<div className="min-w-0">
					<p className="font-semibold uppercase tracking-wide">Before these courses can be submitted</p>
					<div className="mt-1 flex flex-col gap-1">
						{validationIssues.map((issue) => (
							<p key={issue.message}>
								<span className="font-semibold">{issue.message}</span>{" "}
								<span>Affected classes: {issue.classIds.join(", ")}.</span>
							</p>
						))}
						{hasMixedRoleCourses && (
							<p>
								<span className="font-semibold">
									Selected courses contain a mixture of TA and non-TA instructors.
								</span>{" "}
								<span>Affected classes: {formatCourseIdList(mixedRoleCourses)}.</span>
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function CollapsibleBulkEditSection({
	title,
	subtitle,
	summary,
	open,
	onToggle,
	children,
	hasWarning = false,
	sectionMaxHeightClass = "max-h-[36dvh]",
	sectionRef,
}) {
	return (
		<section
			ref={sectionRef}
			className={`${PANEL_CARD_CLASS} min-h-0 shrink-0 overflow-hidden ${
				open ? `flex flex-col ${sectionMaxHeightClass}` : ""
			}`}
		>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				className="flex w-full shrink-0 items-start justify-between gap-3 px-3 py-3 text-left transition hover:bg-gray-50"
			>
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<p className={PANEL_SECTION_LABEL_CLASS}>{title}</p>
						{hasWarning ? (
							<span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
								Needs review
							</span>
						) : null}
					</div>
					{subtitle ? <p className="mt-1 text-xs text-gray-600">{subtitle}</p> : null}
					{summary ? <p className="mt-1 text-xs font-medium text-gray-700">{summary}</p> : null}
				</div>
				<span className="mt-0.5 shrink-0 text-gray-600">
					{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</span>
			</button>
			{open ? (
				<div
					className="min-h-0 flex-1 overflow-y-auto border-t border-gray-200 bg-digitalWhite px-3 py-3"
				>
					{children}
				</div>
			) : null}
		</section>
	);
}

function BulkEditSection({
	selectedBulkCourses,
	selectedBulkClassIds,
	onBulkAddInstructorToSelection,
	onBulkRemoveInstructorFromSelection,
	onBulkRemoveRoleFromSelection,
	bulkFieldSummaries,
	bulkSummaryMessages,
	bulkEditDraft,
	handleBulkDraftSurveyStartChange,
	handleBulkDraftSurveyEndChange,
	handleBulkDraftBlockIdChange,
	allBlockItemsTA,
	rows,
	someAreTA,
	allCoursesNonLEC,
	someNotTA,
	submitTAOnly,
	setSubmitTAOnly,
	bulkDraftBlockedByInstructorCoverage,
	bulkDraftHasInvalidRange,
	bulkDraftHasInvalidBlockRole,
	bulkDraftExamConflictCourses = [],
	bulkDraftInvalidRoleCourses,
	invalidAssignedBlockCourses,
	cannotSubmitList,
	mixedRoleCourses,
	focusRequest,
}) {
	const [expandedSections, setExpandedSections] = useState({
		instructors: true,
		form: false,
		dates: false,
	});
	const instructorSectionRef = useRef(null);
	const formSectionRef = useRef(null);
	const datesSectionRef = useRef(null);
	const sectionRefs = useMemo(
		() => ({
			instructors: instructorSectionRef,
			form: formSectionRef,
			dates: datesSectionRef,
		}),
		[],
	);

	const toggleBulkEditSection = (sectionName) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionName]: !prev[sectionName],
		}));
	};

	const missingBlockCourses = useMemo(
		() => selectedBulkCourses.filter((course) => !course.form && !course.blkid),
		[selectedBulkCourses],
	);
	const missingSurveyDateCourses = useMemo(
		() =>
			selectedBulkCourses.filter(
				(course) => !course.survey_start || !course.survey_end,
			),
		[selectedBulkCourses],
	);
	const invalidSurveyDateCourses = useMemo(
		() =>
			selectedBulkCourses.filter(
				(course) =>
					course.survey_start &&
					course.survey_end &&
					course.survey_start >= course.survey_end,
			),
		[selectedBulkCourses],
	);

	useEffect(() => {
		if (!focusRequest?.section) {
			return;
		}

		setExpandedSections((prev) => ({
			...prev,
			[focusRequest.section]: true,
		}));

		window.setTimeout(() => {
			sectionRefs[focusRequest.section]?.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 0);
	}, [focusRequest, sectionRefs]);

	return (
		<div className="min-h-0 flex-1 overflow-x-hidden">
			<div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto overflow-x-hidden pr-2">
				<ValidationIssuesAlert
					validation={cannotSubmitList}
					mixedRoleCourses={mixedRoleCourses}
				/>
				<CollapsibleBulkEditSection
					title="Instructors"
					subtitle="Add or remove instructors and roles across the selected courses."
					summary={`${selectedBulkClassIds.length} courses selected`}
					open={expandedSections.instructors}
					onToggle={() => toggleBulkEditSection("instructors")}
					hasWarning={bulkDraftBlockedByInstructorCoverage}
					sectionMaxHeightClass="max-h-[70dvh]"
					sectionRef={sectionRefs.instructors}
				>
					<BulkInstructorManager
						selectedBulkCourses={selectedBulkCourses}
						selectedBulkClassIds={selectedBulkClassIds}
						onAddInstructorToSelection={onBulkAddInstructorToSelection}
						onRemoveInstructorFromSelection={onBulkRemoveInstructorFromSelection}
						onRemoveRoleFromSelection={onBulkRemoveRoleFromSelection}
						embedded
					/>
				</CollapsibleBulkEditSection>
				{bulkDraftBlockedByInstructorCoverage && (
					<div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
						Changes to survey dates or block ID are blocked until all selected courses have at least
						one instructor assigned.
					</div>
				)}
				{invalidAssignedBlockCourses.length > 0 && (
					<div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
						Some selected courses already have TA-only blocks assigned while still containing non-TA
						instructors: {formatCourseIdList(invalidAssignedBlockCourses)}.
					</div>
				)}
				<CollapsibleBulkEditSection
					title="Block / Form"
					subtitle="Leave this blank to keep each course's current block assignment."
					summary={
						bulkEditDraft.form
							? `Will apply: ${bulkEditDraft.form}`
							: bulkSummaryMessages.form
					}
					open={expandedSections.form}
					onToggle={() => toggleBulkEditSection("form")}
					hasWarning={
						bulkDraftHasInvalidBlockRole ||
						invalidAssignedBlockCourses.length > 0 ||
						missingBlockCourses.length > 0
					}
					sectionMaxHeightClass="max-h-[36dvh]"
					sectionRef={sectionRefs.form}
				>
					<div className="flex flex-col gap-2">
						{missingBlockCourses.length > 0 && (
							<div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
								<span className="font-semibold">Block ID missing.</span> Select a block ID and
								apply changes before submitting. Affected classes:{" "}
								{formatCourseIdList(missingBlockCourses)}.
							</div>
						)}
						<BlockIdSelect
							value={bulkEditDraft.form}
							inBatch
							selectedBulkClassIds={selectedBulkClassIds}
							rows={rows}
							blockIds={allBlockItemsTA}
							width="100%"
							marginTop="0px"
							marginBottom="0px"
							fontSize="9.5pt"
							placeholderOptionLabel="No block ID change"
							onChange={handleBulkDraftBlockIdChange}
							error={bulkDraftHasInvalidBlockRole}
							helperText={
								bulkDraftHasInvalidBlockRole
									? `TA-only blocks cannot be applied to selections that include non-TA instructors. Affected courses: ${formatCourseIdList(
											bulkDraftInvalidRoleCourses,
										)}.`
									: undefined
							}
						/>
						<p
							className={`text-xs ${
								missingBlockCourses.length > 0
									? "font-semibold text-amber-800"
									: bulkFieldSummaries.form.isMixed
										? "text-amber-700"
										: "text-gray-600"
							}`}
						>
							{bulkSummaryMessages.form}
						</p>
						{bulkEditDraft.form && (
							<p className="text-xs font-medium text-green-700">Will apply: {bulkEditDraft.form}</p>
						)}
						{bulkDraftHasInvalidBlockRole && (
							<div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
								Choose a non-TA block or remove the mixed-role courses from this bulk selection.
							</div>
						)}
						{someAreTA && allCoursesNonLEC && someNotTA && (
							<div className="pt-1">
								<FormControlLabel
									className="text-zinc-900 text-sm font-semibold"
									control={
										<Checkbox
											checked={submitTAOnly}
											onChange={(event) => {
												setSubmitTAOnly(event.target.checked);
											}}
										/>
									}
									label={
										<CustomTooltip title="Checking this option will submit only the TA roles within the selected courses. This is to streamline the SPOT ordering process for LAB and SEM sections that contain a mix of TA and non-TA teaching roles. Any role apart from TAs will be excluded, and those roles will not be surveyed. To survey the non-TA roles, please instance/duplicate the course and submit them separately.">
											<span className="underline text-primary">Submit TAs Only*</span>
										</CustomTooltip>
									}
								/>
							</div>
						)}
					</div>
				</CollapsibleBulkEditSection>

				<CollapsibleBulkEditSection
					title="Survey Dates"
					subtitle="Only choose the date fields you want to overwrite across the selection."
					summary={
						bulkEditDraft.surveyStart || bulkEditDraft.surveyEnd
							? [
									bulkEditDraft.surveyStart
										? `Start: ${formatBulkDate(bulkEditDraft.surveyStart)}`
										: null,
									bulkEditDraft.surveyEnd
										? `End: ${formatBulkDate(bulkEditDraft.surveyEnd)}`
										: null,
								]
									.filter(Boolean)
									.join(" | ")
							: `${bulkSummaryMessages.surveyStart} ${bulkSummaryMessages.surveyEnd}`
					}
					open={expandedSections.dates}
					onToggle={() => toggleBulkEditSection("dates")}
					hasWarning={
						bulkDraftHasInvalidRange ||
						bulkDraftExamConflictCourses.length > 0 ||
						missingSurveyDateCourses.length > 0 ||
						invalidSurveyDateCourses.length > 0
					}
					sectionMaxHeightClass="max-h-[42dvh]"
					sectionRef={sectionRefs.dates}
				>
					<div className="flex flex-col gap-3">
						
						{invalidSurveyDateCourses.length > 0 && (
							<div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
								<span className="font-semibold">Survey date range invalid.</span> Survey start
								must be before survey end. Affected classes:{" "}
								{formatCourseIdList(invalidSurveyDateCourses)}.
							</div>
						)}
						{bulkDraftExamConflictCourses.length > 0 && (
							<div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
								<span className="font-semibold">Survey end conflicts with final exam.</span>{" "}
								Surveys must close at least one day before the final exam. Affected classes:{" "}
								{formatCourseIdList(bulkDraftExamConflictCourses)}.
							</div>
						)}
						<div className="grid w-full gap-3 xl:grid-cols-2">
						<div
							className={`w-full rounded-lg border px-2 py-2 ${
								missingSurveyDateCourses.length > 0
									? "border-amber-300 bg-amber-50"
									: bulkFieldSummaries.surveyStart.isMixed
									? "border-amber-300 bg-amber-50"
									: "border-gray-200 bg-white"
							}`}
						>
							<DatePickerComponent
								width="100%"
								label="Survey Start Date"
								value={bulkEditDraft.surveyStart}
								onChange={handleBulkDraftSurveyStartChange}
							/>
							<div className="px-1 pb-1">
								<p
									className={`text-xs ${
										missingSurveyDateCourses.length > 0
											? "font-semibold text-amber-800"
											: bulkFieldSummaries.surveyStart.isMixed ? "text-amber-700" : "text-gray-600"
									}`}
								>
									{bulkSummaryMessages.surveyStart}
								</p>
								{bulkEditDraft.surveyStart && (
									<p className="mt-1 text-xs font-medium text-green-700">
										Will apply: {formatBulkDate(bulkEditDraft.surveyStart)}
									</p>
								)}
							</div>
						</div>

						<div
							className={`w-full rounded-lg border px-2 py-2 ${
								missingSurveyDateCourses.length > 0
									? "border-amber-300 bg-amber-50"
									: bulkDraftExamConflictCourses.length > 0
										? "border-red-200 bg-red-50"
									: bulkFieldSummaries.surveyEnd.isMixed
									? "border-amber-300 bg-amber-50"
									: "border-gray-200 bg-white"
							}`}
						>
							<DatePickerComponent
								width="100%"
								label="Survey End Date"
								value={bulkEditDraft.surveyEnd}
								onChange={handleBulkDraftSurveyEndChange}
							/>
							<div className="px-1 pb-1">
								<p
									className={`text-xs ${
										missingSurveyDateCourses.length > 0
											? "font-semibold text-amber-800"
											: bulkDraftExamConflictCourses.length > 0
												? "font-semibold text-red-700"
											: bulkFieldSummaries.surveyEnd.isMixed ? "text-amber-700" : "text-gray-600"
									}`}
								>
									{bulkSummaryMessages.surveyEnd}
								</p>
								{bulkEditDraft.surveyEnd && (
									<p className="mt-1 text-xs font-medium text-green-700">
										Will apply: {formatBulkDate(bulkEditDraft.surveyEnd)}
									</p>
								)}
							</div>
						</div>
					</div>
					</div>

					{bulkDraftHasInvalidRange && (
						<div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
							Survey start must be before survey end before you can apply these bulk edits.
						</div>
					)}
					{bulkDraftExamConflictCourses.length > 0 && (
						<div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
							Move the survey end date to at least one day before every affected final exam before
							you can apply these bulk edits.
						</div>
					)}
				</CollapsibleBulkEditSection>
			</div>
		</div>
	);
}

export default function ClassPanel({
	isTermActive,
	isAdmin,
	validateClassList,
	rows,
	effectiveRows,
	updateRowsBatch,
	reviewingClasses,
	handleCancelSubmitAllSuitable,
	handleConfirmSubmitAllSuitable,
	submitAllLoading,
	confirmSubmitAllSelected,
	handleCancelSubmitAllSelected,
	handleReviewAllSelected,
	handleSubmitAllSelected,
	handleClearSelection,
	listOfSuitableCourses,
	setBatchUpdated,
	batchUpdated,
	allBlockItemsTA,
	deleteClassMutation,
	token,
	someNotTA,
	someAreTA,
	submitTAOnly,
	setSubmitTAOnly,
	allCoursesNonLEC,
	selectedBulkClassIds,
	onBulkAddInstructorToSelection,
	onBulkRemoveInstructorFromSelection,
	onBulkRemoveRoleFromSelection,
	openBulkEdit,
	setOpenBulkEdit,
}) {
	const [selectionModel, setSelectionModel] = useAtom(spotOrderingSelectionModelAtom);
	const [listOfUniqueBlockIDs, setListOfBlockIDs] = useState([]);
	const [listOfUniqueDates, setListOfUniqueDates] = useState([]);
	const [selectedClass] = useAtom(selectedClassAtom);
	const [, setInstanceRows] = useAtom(instanceRowsAtom);
	const [unsavedChanges, setUnsavedChanges] = useAtom(unsavedChangesAtom);
	const [bulkEditDraft, setBulkEditDraft] = useState(EMPTY_BULK_EDIT_DRAFT);
	const [bulkInstructorSnapshotMap, setBulkInstructorSnapshotMap] = useState({});
	const [bulkEditOpenedWithUnsavedChanges, setBulkEditOpenedWithUnsavedChanges] = useState(false);
	const [bulkEditOpenedWithBatchUpdates, setBulkEditOpenedWithBatchUpdates] = useState(false);
	const [bulkEditHasAppliedChanges, setBulkEditHasAppliedChanges] = useState(false);
	const [bulkEditFocusRequest, setBulkEditFocusRequest] = useState(null);

	const effectiveRowsById = useMemo(
		() => new Map((effectiveRows ?? []).map((row) => [row.classid, row])),
		[effectiveRows],
	);

	useEffect(() => {
		if (selectedClass) {
			let listOfClasses = [];
			if (!selectedClass.original) {
				listOfClasses.push(selectedClass);
			}
			if (rows && rows.length > 0) {
				rows.forEach((row) => {
					if (row.original === selectedClass.classid && !selectedClass.original) {
						listOfClasses.push(row);
					} else if (
						selectedClass.original &&
						(row.classid === selectedClass.original || row.original === selectedClass.original)
					) {
						listOfClasses.push(row);
					}
				});
			}
			setInstanceRows(listOfClasses);
		}
	}, [selectedClass, rows, setInstanceRows]);

	useEffect(() => {
		if (listOfSuitableCourses && listOfSuitableCourses.length > 0) {
			const uniqueBlockIDs = new Set(listOfSuitableCourses.map((course) => course.form));
			const dateTuples = listOfSuitableCourses.map((course) => [
				course.survey_start,
				course.survey_end,
			]);
			const uniqueDates = new Set(dateTuples.map((date) => date.join(" to ")));
			setListOfBlockIDs([...uniqueBlockIDs]);
			setListOfUniqueDates([...uniqueDates]);
		}
	}, [listOfSuitableCourses]);

	const selectedBulkCourses = useMemo(
		() => selectedBulkClassIds.map((classid) => effectiveRowsById.get(classid)).filter(Boolean),
		[selectedBulkClassIds, effectiveRowsById],
	);

	const missingInstructorCourses = useMemo(
		() => selectedBulkCourses.filter((course) => !course.roles || course.roles.length === 0),
		[selectedBulkCourses],
	);
	const hasMissingInstructorCourses = missingInstructorCourses.length > 0;
	const selectedBulkCoursesWithNonTAs = useMemo(
		() => selectedBulkCourses.filter(courseHasNonTAInstructors),
		[selectedBulkCourses],
	);
	const selectedBulkCoursesWithMixedRoles = useMemo(
		() => getMixedTARoleCourses(selectedBulkCourses),
		[selectedBulkCourses],
	);

	const blockIDsCustomCheckbox = useMemo(
		() => [...new Set(selectedBulkCourses.map((row) => row.form || row.blkid).filter(Boolean))],
		[selectedBulkCourses],
	);
	const uniqueDatesCustomCheckbox = useMemo(
		() => [
			...new Set(
				selectedBulkCourses.map((row) =>
					[formatBulkDate(row.survey_start), formatBulkDate(row.survey_end)].join(" to "),
				),
			),
		],
		[selectedBulkCourses],
	);

	const bulkFieldSummaries = useMemo(
		() => ({
			surveyStart: summarizeBulkField(selectedBulkCourses.map((course) => course.survey_start)),
			surveyEnd: summarizeBulkField(selectedBulkCourses.map((course) => course.survey_end)),
			form: summarizeBulkField(selectedBulkCourses.map((course) => course.form || course.blkid)),
		}),
		[selectedBulkCourses],
	);

	const bulkSummaryMessages = useMemo(
		() => ({
			surveyStart: getBulkSummaryMessage(bulkFieldSummaries.surveyStart, {
				fieldLabel: "survey start dates",
				formatter: formatBulkDate,
				emptyLabel: "no survey start date set",
			}),
			surveyEnd: getBulkSummaryMessage(bulkFieldSummaries.surveyEnd, {
				fieldLabel: "survey end dates",
				formatter: formatBulkDate,
				emptyLabel: "no survey end date set",
			}),
			form: getBulkSummaryMessage(bulkFieldSummaries.form, {
				fieldLabel: "block IDs",
				emptyLabel: "no block ID set",
			}),
		}),
		[bulkFieldSummaries],
	);

	const hasBulkDraftChanges = Boolean(
		bulkEditDraft.surveyStart || bulkEditDraft.surveyEnd || bulkEditDraft.form,
	);
	const bulkDraftChangeCount = [
		bulkEditDraft.surveyStart,
		bulkEditDraft.surveyEnd,
		bulkEditDraft.form,
	].filter(Boolean).length;
	const hasPendingInstructorChanges = useMemo(
		() =>
			selectedBulkCourses.some(
				(course) =>
					!instructorStateMatchesSnapshot(course, bulkInstructorSnapshotMap[course.classid]),
			),
		[selectedBulkCourses, bulkInstructorSnapshotMap],
	);
	const hasPendingBulkChanges = hasBulkDraftChanges || hasPendingInstructorChanges;
	const pendingBulkChangeCount = bulkDraftChangeCount + (hasPendingInstructorChanges ? 1 : 0);

	const applyBulkButtonLabel = hasPendingBulkChanges
		? `Apply ${pendingBulkChangeCount} change${
				pendingBulkChangeCount === 1 ? "" : "s"
			} to ${selectedBulkClassIds.length} course${selectedBulkClassIds.length === 1 ? "" : "s"}`
		: `Apply changes to ${selectedBulkClassIds.length} course${
				selectedBulkClassIds.length === 1 ? "" : "s"
			}`;

	const bulkDraftHasInvalidRange =
		bulkEditDraft.surveyStart &&
		bulkEditDraft.surveyEnd &&
		!bulkEditDraft.surveyStart.isBefore(bulkEditDraft.surveyEnd, "day");
	const bulkDraftExamConflictCourses = useMemo(
		() =>
			bulkEditDraft.surveyEnd
				? selectedBulkCourses.filter((course) =>
						surveyEndConflictsWithExam(course, bulkEditDraft.surveyEnd),
					)
				: [],
		[selectedBulkCourses, bulkEditDraft.surveyEnd],
	);
	const bulkDraftBlockedByInstructorCoverage = openBulkEdit && hasMissingInstructorCourses;
	const bulkDraftSelectedBlock = bulkEditDraft.form ? allBlockItemsTA?.[bulkEditDraft.form] : null;
	const bulkDraftHasInvalidBlockRole = Boolean(
		bulkEditDraft.form &&
		isTAOnlyBlockOption(bulkDraftSelectedBlock) &&
		selectedBulkCoursesWithNonTAs.length > 0,
	);
	const invalidAssignedBlockCourses = useMemo(
		() =>
			selectedBulkCourses.filter((course) => {
				const assignedBlock = allBlockItemsTA?.[course.form || course.blkid];
				return isTAOnlyBlockOption(assignedBlock) && courseHasNonTAInstructors(course);
			}),
		[selectedBulkCourses, allBlockItemsTA],
	);

	const resetBulkEditSession = () => {
		setBulkInstructorSnapshotMap({});
		setBulkEditOpenedWithUnsavedChanges(false);
		setBulkEditOpenedWithBatchUpdates(false);
		setBulkEditHasAppliedChanges(false);
	};

	const captureBulkInstructorSnapshot = (courses = selectedBulkCourses) => {
		setBulkInstructorSnapshotMap(createInstructorSnapshotMap(courses));
	};

	useEffect(() => {
		if (selectedBulkClassIds.length === 0) {
			setOpenBulkEdit(false);
			setBulkEditDraft(EMPTY_BULK_EDIT_DRAFT);
			resetBulkEditSession();
		}
	}, [selectedBulkClassIds.length, setOpenBulkEdit]);

	const clearBulkEditDraft = () => {
		setBulkEditDraft(EMPTY_BULK_EDIT_DRAFT);
	};

	const handleOpenBulkEdit = () => {
		clearBulkEditDraft();
		captureBulkInstructorSnapshot();
		setBulkEditOpenedWithUnsavedChanges(unsavedChanges);
		setBulkEditOpenedWithBatchUpdates(batchUpdated);
		setBulkEditHasAppliedChanges(false);
		setOpenBulkEdit(true);
	};

	const handleCloseBulkEdit = () => {
		const rollbackUpdates = selectedBulkCourses
			.filter(
				(course) =>
					!instructorStateMatchesSnapshot(course, bulkInstructorSnapshotMap[course.classid]),
			)
			.map((course) => {
				const snapshot = bulkInstructorSnapshotMap[course.classid];

				return {
					classid: course.classid,
					updates: {
						roles: cloneCourseRoles(snapshot?.roles),
						name: snapshot?.name || "",
						empid: snapshot?.empid || "",
						class_role: snapshot?.class_role || "",
						teamsize: snapshot?.teamsize ?? 0,
						teamName: snapshot?.teamName || "",
					},
				};
			});

		if (rollbackUpdates.length > 0) {
			updateRowsBatch(rollbackUpdates);
		}

		setBatchUpdated(bulkEditOpenedWithBatchUpdates || bulkEditHasAppliedChanges);
		setUnsavedChanges(bulkEditOpenedWithUnsavedChanges || bulkEditHasAppliedChanges);
		clearBulkEditDraft();
		resetBulkEditSession();
		setOpenBulkEdit(false);
	};

	const handleBulkDraftBlockIdChange = (event) => {
		setBulkEditDraft((prev) => ({
			...prev,
			form: event.target.value,
		}));
	};

	const handleBulkDraftSurveyStartChange = (date) => {
		setBulkEditDraft((prev) => ({
			...prev,
			surveyStart: date,
		}));
	};

	const handleBulkDraftSurveyEndChange = (date) => {
		setBulkEditDraft((prev) => ({
			...prev,
			surveyEnd: date,
		}));
	};

	const handleApplyBulkEdits = () => {
		if (
			!hasPendingBulkChanges ||
			bulkDraftHasInvalidRange ||
			bulkDraftHasInvalidBlockRole ||
			bulkDraftExamConflictCourses.length > 0
		) {
			return;
		}

		const sharedUpdates = {};

		if (bulkEditDraft.surveyStart) {
			sharedUpdates.survey_start = bulkEditDraft.surveyStart.format("YYYY-MM-DD");
		}

		if (bulkEditDraft.surveyEnd) {
			sharedUpdates.survey_end = bulkEditDraft.surveyEnd.format("YYYY-MM-DD");
		}

		if (bulkEditDraft.form) {
			sharedUpdates.blkid = bulkEditDraft.form;
			sharedUpdates.form = bulkEditDraft.form;
		}

		if (Object.keys(sharedUpdates).length > 0) {
			updateRowsBatch(
				selectedBulkClassIds.map((classid) => ({
					classid,
					updates: sharedUpdates,
				})),
			);
		}
		const projectedCourses = selectedBulkCourses.map((course) => ({
			...course,
			...sharedUpdates,
		}));
		const nextIssueSection = getBulkIssueSection(projectedCourses, allBlockItemsTA);

		if (nextIssueSection) {
			setBulkEditFocusRequest({
				key: Date.now(),
				section: nextIssueSection,
			});
		}

		setBatchUpdated(true);
		setUnsavedChanges(true);
		setBulkEditHasAppliedChanges(true);
		captureBulkInstructorSnapshot();
		clearBulkEditDraft();
	};

	const classList = (effectiveRows ?? []).filter((row) =>
		selectedBulkClassIds.includes(row.classid),
	);
	const cannotSubmitList = validateClassList(classList);

	const handleRemoveBulkSelection = (classidToRemove) => {
		if (unsavedChanges) {
			return;
		}

		const nextSelectedIds = getSelectedIdsFromModel(selectionModel, rows).filter(
			(classid) => classid !== classidToRemove,
		);

		setSelectionModel({
			type: "include",
			ids: new Set(nextSelectedIds),
		});
	};

	const [classForEvaluatedDialog] = useState(null);
	const [evaluationDialogState] = useState("");
	const [openEvaluationDialog, setOpenEvaluationDialog] = useState(false);

	const handleEvaluatedDialogConfirmation = async (classForConfirm) => {
		const classid = classForConfirm.classid;

		await deleteClassMutation.mutateAsync({
			token,
			classid,
		});

		setOpenEvaluationDialog(false);
	};

	return (
		<div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-gray-300 bg-white p-2 shadow-xl drop-shadow-sm">
			<ConfirmExclusionDialog
				open={openEvaluationDialog}
				setOpen={setOpenEvaluationDialog}
				evaluationDialogState={evaluationDialogState}
				classForEvaluatedDialog={classForEvaluatedDialog}
				handleEvaluatedDialogConfirmation={handleEvaluatedDialogConfirmation}
			/>

			{reviewingClasses ? (
				<div className="flex h-full min-h-0 flex-col">
					<CompactPanelHeader
						eyebrow="Department Review"
						title="Review whole department submission"
						subtitle="The table is sorted by suitability. Review the summary below, then confirm submission."
						badges={[
							{
								label: `${listOfSuitableCourses.length} of ${rows.length} suitable`,
								className:
									listOfSuitableCourses.length !== rows.length
										? "bg-red-100 text-red-700"
										: "bg-green-100 text-green-700",
							},
						]}
					/>

					<div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
						<div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
							Please review the classes at the bottom of the table carefully before submitting the
							entire department.
						</div>
						<div className="grid min-h-0 flex-1 gap-3 xl:grid-cols-2">
							<SummaryCard
								title="Block IDs Used"
								values={listOfUniqueBlockIDs}
								emptyLabel="No block IDs in this review set."
							/>
							<SummaryCard
								title="Unique Survey Windows"
								values={listOfUniqueDates}
								emptyLabel="No survey windows in this review set."
							/>
						</div>
					</div>

					<div className="mt-3 shrink-0 border-t border-gray-200 pt-3">
						<div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
							<Button
								disabled={!isTermActive && !isAdmin}
								onClick={handleCancelSubmitAllSuitable}
								variant="outlined"
								color="error"
								sx={{
									width: "100%",
									"@media (min-width:1280px)": { width: "auto" },
								}}
							>
								Cancel
							</Button>
							<Button
								disabled={!isTermActive && !isAdmin}
								onClick={handleConfirmSubmitAllSuitable}
								variant="contained"
								color="success"
								sx={{
									width: "100%",
									"@media (min-width:1280px)": { width: "auto" },
								}}
							>
								{submitAllLoading ? (
									<div className="flex items-center justify-center gap-2">
										<p>Please wait</p>
										<CircularProgress size={18} sx={{ color: "white" }} />
									</div>
								) : (
									"Confirm Submission"
								)}
							</Button>
						</div>
					</div>
				</div>
			) : selectedBulkClassIds.length > 0 ? (
				<div className="flex h-full min-h-0 flex-col relative">
					<div className="absolute -top-3 right-0">
						<button
							onClick={handleClearSelection}
							disabled={(!isTermActive && !isAdmin) || submitAllLoading}
							className="text-xs  hover:text-red-900 text-red-800 hover:underline disabled:text-gray-400 disabled:hover:underline-none"
						>
							Cancel Bulk Operation
						</button>
					</div>
					<div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
						{confirmSubmitAllSelected ? (
							<>
								<ValidationIssuesAlert
									validation={cannotSubmitList}
									mixedRoleCourses={selectedBulkCoursesWithMixedRoles}
								/>
								<div className="grid gap-3 xl:grid-cols-2">
									<SummaryCard
										title="Block IDs"
										values={blockIDsCustomCheckbox}
										emptyLabel="No block IDs are currently set."
									/>
									<SummaryCard
										title="Survey Windows"
										values={uniqueDatesCustomCheckbox}
										emptyLabel="No survey windows are currently set."
									/>
								</div>
								<div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
									All selected courses in the table will be submitted exactly as currently shown.
									Please confirm their accuracy before continuing.
								</div>

								<SelectionListCard
									courses={selectedBulkCourses}
									onRemove={handleRemoveBulkSelection}
									isEditMode
									allowRemoval={false}
								/>
							</>
						) : (
							<>
								{!openBulkEdit && (
									<SelectionListCard
										courses={selectedBulkCourses}
										onRemove={handleRemoveBulkSelection}
										isEditMode={openBulkEdit}
										disableRemoval={unsavedChanges}
									/>
								)}

								{openBulkEdit ? (
									<BulkEditSection
										selectedBulkCourses={selectedBulkCourses}
										selectedBulkClassIds={selectedBulkClassIds}
										onBulkAddInstructorToSelection={onBulkAddInstructorToSelection}
										onBulkRemoveInstructorFromSelection={onBulkRemoveInstructorFromSelection}
										onBulkRemoveRoleFromSelection={onBulkRemoveRoleFromSelection}
										bulkFieldSummaries={bulkFieldSummaries}
										bulkSummaryMessages={bulkSummaryMessages}
										bulkEditDraft={bulkEditDraft}
										handleBulkDraftSurveyStartChange={handleBulkDraftSurveyStartChange}
										handleBulkDraftSurveyEndChange={handleBulkDraftSurveyEndChange}
										handleBulkDraftBlockIdChange={handleBulkDraftBlockIdChange}
										allBlockItemsTA={allBlockItemsTA}
										rows={rows}
										someAreTA={someAreTA}
										allCoursesNonLEC={allCoursesNonLEC}
										someNotTA={someNotTA}
										submitTAOnly={submitTAOnly}
										setSubmitTAOnly={setSubmitTAOnly}
										bulkDraftBlockedByInstructorCoverage={bulkDraftBlockedByInstructorCoverage}
										bulkDraftHasInvalidRange={bulkDraftHasInvalidRange}
										bulkDraftHasInvalidBlockRole={bulkDraftHasInvalidBlockRole}
										bulkDraftExamConflictCourses={bulkDraftExamConflictCourses}
										bulkDraftInvalidRoleCourses={selectedBulkCoursesWithNonTAs}
										invalidAssignedBlockCourses={invalidAssignedBlockCourses}
										cannotSubmitList={cannotSubmitList}
										mixedRoleCourses={selectedBulkCoursesWithMixedRoles}
										focusRequest={bulkEditFocusRequest}
									/>
								) : (
									<>
										<ValidationIssuesAlert
											validation={cannotSubmitList}
											mixedRoleCourses={selectedBulkCoursesWithMixedRoles}
										/>
										<div className={`${PANEL_MUTED_CARD_CLASS} p-3`}>
											<p className={PANEL_SECTION_LABEL_CLASS}>Next Step</p>
											<p className="mt-2 text-sm font-semibold text-gray-900">
												Edit the selected courses in bulk, or review and submit the current selection.
											</p>
											<p className="mt-1 text-xs leading-5 text-gray-600">
												Use the buttons below once you have selected all of the courses you want to
												edit or submit together.
											</p>
										</div>
									</>
								)}
							</>
						)}
					</div>

					<div className="mt-3 shrink-0 border-t border-gray-400 p-1  pt-2  ">
						{confirmSubmitAllSelected ? (
							<div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
								<Button
									onClick={handleCancelSubmitAllSelected}
									disabled={(!isTermActive && !isAdmin) || submitAllLoading}
									variant="outlined"
									color="error"
									startIcon={<CancelIcon />}
									sx={{
										width: "100%",
										"@media (min-width:1280px)": { width: "auto" },
									}}
								>
									{openBulkEdit ? "Return to Edit" : "Back to Selection"}
								</Button>
								<Button
									onClick={handleSubmitAllSelected}
									disabled={(!isTermActive && !isAdmin) || submitAllLoading}
									variant="contained"
									color="success"
									startIcon={!submitAllLoading ? <PublishIcon /> : null}
									sx={{
										width: "100%",
										"@media (min-width:1280px)": { width: "auto" },
									}}
								>
									{submitAllLoading ? (
										<div className="flex items-center justify-center gap-2">
											<p>Please wait</p>
											<CircularProgress size={18} sx={{ color: "white" }} />
										</div>
									) : (
										`Submit ${selectedBulkClassIds.length} course${
											selectedBulkClassIds.length === 1 ? "" : "s"
										}`
									)}
								</Button>
							</div>
						) : openBulkEdit ? (
							<div className="flex flex-col items-stretch gap-2">
								<div className="flex flex-col gap-2 xl:flex-row xl:justify-between">
									<span className="flex-1 xl:flex-none">
										<Button
											variant="outlined"
											disabled={!hasBulkDraftChanges}
											onClick={clearBulkEditDraft}
											sx={{
												width: "100%",
												"@media (min-width:1280px)": { width: "auto" },
											}}
										>
											Clear Draft
										</Button>
									</span>
									<span className="flex-1 xl:flex-none"><Button
										color="success"
										variant="contained"
										disabled={
											!hasPendingBulkChanges ||
											bulkDraftHasInvalidRange ||
											bulkDraftBlockedByInstructorCoverage ||
											bulkDraftHasInvalidBlockRole ||
											bulkDraftExamConflictCourses.length > 0
										}
										onClick={handleApplyBulkEdits}
										sx={{
											width: "100%",
											whiteSpace: "normal",
											// lineHeight: 1.25,
											"@media (min-width:1280px)": {
												width: "auto",
											},
										}}
									>
										{applyBulkButtonLabel}
									</Button></span>
									
								</div>

								<div className="flex w-full items-center justify-between gap-2">
									<span className="flex-1">
										<Button
											variant="outlined"
											onClick={handleCloseBulkEdit}
											color={hasPendingBulkChanges ? "warning" : "primary"}
											fullWidth
										>
											{hasPendingBulkChanges ? "Cancel Changes" : "Back"}
										</Button>
									</span>
									<Tooltip
										title={
											hasPendingBulkChanges
												? "Apply or clear the pending bulk edit changes before reviewing."
												: cannotSubmitList.shouldPrevent
													? cannotSubmitList.message
													: "Review the selected courses as they currently appear in the table."
										}
										placement="top"
									>
										<span className="flex-1">
											<Button
												color="success"
												variant="outlined"
												disabled={hasPendingBulkChanges || cannotSubmitList.shouldPrevent}
												onClick={handleReviewAllSelected}
												endIcon={<ArrowForwardIcon />}
												fullWidth
											>
												Next
											</Button>
										</span>
									</Tooltip>
								</div>
							</div>
						) : (
							<div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
								<Tooltip
									title={
										cannotSubmitList.shouldPrevent
											? cannotSubmitList.message
											: "Edit the selected courses together"
									}
									placement="top"
								>
									<span className="flex-1">
										<Button
											fullWidth
											disabled={selectedBulkClassIds.length < 2}
											color={cannotSubmitList.shouldPrevent ? "warning" : "info"}
											variant="contained"
											onClick={handleOpenBulkEdit}
											startIcon={cannotSubmitList.shouldPrevent ? <WarningIcon /> : <EditIcon />}
										>
											Edit
										</Button>
									</span>
								</Tooltip>

								<Tooltip
									title="Review the selected courses as they currently appear in the table."
									placement="top"
								>
									<span className="flex-1">
										<Button
											fullWidth
											onClick={handleReviewAllSelected}
											disabled={cannotSubmitList.shouldPrevent || selectedBulkClassIds.length < 2}
											variant="contained"
											color="success"
											endIcon={<ArrowForwardIcon />}
										>
											Next
										</Button>
									</span>
								</Tooltip>
							</div>
						)}
					</div>
				</div>
			) : (
				<div className="flex h-full w-full items-center justify-center px-4">
					<div className={`${PANEL_MUTED_CARD_CLASS} max-w-xl p-6 text-center`}>
						<p className={PANEL_SECTION_LABEL_CLASS}>Class Panel</p>
						<h3 className="mt-2 text-2xl font-bold text-gray-900">
							Select courses to start bulk editing
						</h3>
						<p className="mt-3 text-sm leading-6 text-gray-600">
							Use the table checkboxes to build a bulk selection. Once courses are selected, this
							panel will help you review, edit shared values, and submit the selection.
						</p>
						<div className="mt-6 grid gap-3 text-left sm:grid-cols-2">
							<div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
								<p className="text-sm font-semibold text-gray-900">Single-course editing</p>
								<p className="mt-1 text-xs text-gray-600">
									Use the Manage action in the table when you only need to edit one course.
								</p>
							</div>
							<div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
								<p className="text-sm font-semibold text-gray-900">Bulk workflow</p>
								<p className="mt-1 text-xs text-gray-600">
									Select multiple courses to unlock shared editing, review, and submission here.
								</p>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
